import gaspumpLogo from './gaspump.png'
import tonfunLogo from './tonfun.png'

// Source brand marks for the avatar badge. Each source shows its real logo
// (cloned verbatim from the Paper Search artboard pills, V10-0) AND a badge
// ring coloured from the mark — a gradient where the mark has 2+ colours
// (uranus blue→green, dedust amber), a solid where it's one (stonfi blue,
// blum white). NEVER invent a mark.
//   Have: STON.fi, DeDust, Uranus, Blum.  Pending (no asset yet): Tonfun.

interface Props {
  source?: string | null
  size?: number
}

// One standard 135° brand gradient per source — [light, base, dark]. The SAME
// gradient drives the avatar ring (SVG stroke), the badge ring (background-clip
// border) and the logo tint, so all three match in colour. Only the colours
// differ. blum is a soft white (not flat #fff). NEVER invent a mark.
const SOURCE_STOPS: Record<string, [string, string, string]> = {
  stonfi: ['#6fa8fa', '#4c8ff4', '#2e6fd4'],
  dedust: ['#ffdb2e', '#ffaa01', '#cc7401'],
  uranus: ['#0070bc', '#2cb8ff', '#00ff95'],
  blum: ['#ffffff', '#e8e9ec', '#c9ccd2'],
  tonfun: ['#a3e635', '#76c442', '#4d8c28'], // brand green (the up-arrow mark)
  // gaspump: рубиновый градиент от бренд-красного насоса (#BD1F27), поднят
  // в сочность (фидбек: «не светлее, а приятнее — а то темно»)
  gaspump: ['#ef5a60', '#d7333c', '#9c151d'],
}
const DEFAULT_STOPS: [string, string, string] = ['#6b7079', '#5a5e66', '#494d54']

// Short, DOM-id-safe key for a source (also the SVG gradient id suffix).
export function sourceKey(source?: string | null): string {
  if (source?.startsWith('stonfi')) return 'stonfi'
  if (source?.startsWith('dedust')) return 'dedust'
  if (source?.startsWith('uranus')) return 'uranus'
  if (source?.startsWith('blum')) return 'blum'
  if (source?.startsWith('tonfun')) return 'tonfun'
  if (source?.startsWith('gaspump')) return 'gaspump'
  return 'default'
}

// 3 gradient stops [light, base, dark] for the source's brand.
export function sourceStops(source?: string | null): [string, string, string] {
  return SOURCE_STOPS[sourceKey(source)] ?? DEFAULT_STOPS
}

// CSS gradient for the badge ring (background-clip border).
export function sourceRing(source?: string | null): string {
  const [a, b, c] = sourceStops(source)
  return `linear-gradient(135deg, ${a}, ${b} 55%, ${c})`
}

// Base brand colour (mid stop) — for flat needs.
export function sourceColor(source?: string | null): string {
  return sourceStops(source)[1]
}

// MIGRATED-from-launchpad treatment (axiom-style): a slightly-yellow gold. The
// badge ring + avatar ring use this gradient and the logo is tinted gold,
// overriding the launchpad's own colours to signal "graduated to a DEX".
export const GOLD_STOPS: [string, string, string] = ['#ffe08a', '#f5c12b', '#cf9415']
export function goldRing(): string {
  const [a, b, c] = GOLD_STOPS
  return `linear-gradient(135deg, ${a}, ${b} 55%, ${c})`
}

export function SourceLogo({ source, size = 12 }: Props) {
  if (source?.startsWith('stonfi')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="12 11.5 41 41"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M36.955 40.981C37.202 40.818 37.543 40.923 37.644 41.193L41.475 51.416C41.598 51.745 41.288 52.072 40.934 51.986L26.828 48.567C26.449 48.475 26.359 47.999 26.681 47.786L36.955 40.981Z"
          fill="var(--src-stonfi)"
        />
        <path
          d="M39.432 38.725C39.415 38.678 39.406 38.628 39.406 38.578V28.274C39.406 27.983 39.113 27.773 38.82 27.856L26.733 31.273C26.441 31.356 26.148 31.146 26.148 30.855V24.196C26.148 24.06 26.215 23.931 26.328 23.849L42.079 12.366C42.298 12.206 42.613 12.267 42.751 12.496L52.266 28.341C52.333 28.454 52.345 28.589 52.298 28.71L44.287 49.404C44.139 49.786 43.574 49.783 43.43 49.4L39.432 38.725Z"
          fill="var(--src-stonfi)"
        />
        <path
          d="M22.115 13.259C22.191 13.188 22.291 13.144 22.397 13.136L36.935 12.002C37.389 11.966 37.61 12.52 37.248 12.784L24.857 21.801C24.742 21.885 24.593 21.912 24.454 21.873L16.295 19.623C15.959 19.531 15.852 19.126 16.102 18.892L22.115 13.259Z"
          fill="var(--src-stonfi)"
        />
        <path
          d="M14.022 21.887C14.057 21.621 14.332 21.448 14.601 21.523L23.248 23.915C23.444 23.968 23.578 24.139 23.578 24.334V34.018C23.578 34.31 23.871 34.519 24.163 34.436L36.166 31.034C36.459 30.951 36.752 31.161 36.752 31.453V37.604C36.752 37.747 36.679 37.881 36.556 37.962L23.093 46.914C22.918 47.031 22.681 47.016 22.524 46.877L12.147 37.712C12.039 37.616 11.985 37.477 12.004 37.337L14.022 21.887Z"
          fill="var(--src-stonfi)"
        />
      </svg>
    )
  }
  if (source?.startsWith('uranus')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="-25 -235 1540 1540"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="wt-uranus"
            x1="1285"
            y1="535"
            x2="215"
            y2="535"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0070BC" />
            <stop offset="0.5" stopColor="#2CB8FF" />
            <stop offset="1" stopColor="#00FF95" />
          </linearGradient>
        </defs>
        <path
          d="M616.348 778.917C631.159 785.566 646.067 792.152 661.06 798.666C716.081 822.568 770.549 844.786 823.952 865.185C822.564 865.835 821.175 866.484 819.784 867.129C809.084 872.265 804.375 884.891 809.511 895.591C813.149 903.509 820.854 908.003 828.772 908.003C831.767 908.003 834.977 907.362 837.759 906.078C851.772 899.56 865.613 892.799 879.269 885.802C950.219 911.583 1018.91 933.962 1084.09 952.594C992.496 1026.02 876.315 1070 750.019 1070C660.995 1070 577.107 1047.96 503.277 1009.44C578.605 995.958 651.151 977.34 719.417 953.8C730.545 949.948 736.323 937.75 732.472 926.622C728.62 915.494 716.421 909.502 705.294 913.354C624.616 941.174 537.731 961.931 446.567 975.199C397.989 941.601 354.975 900.086 319.879 852.578C430.135 837.426 528.645 811.605 616.348 778.917Z"
          fill="url(#wt-uranus)"
        />
        <path
          d="M229.356 576.838C328.39 637.385 440.725 697.305 560.508 753.327C480.419 780.061 391.523 800.87 293.128 812.987C250.542 743.223 223.578 662.973 216.944 577.159C221.083 577.076 225.221 576.967 229.356 576.838Z"
          fill="url(#wt-uranus)"
        />
        <path
          d="M1220.82 281.196C1225.4 289.658 1229.76 298.255 1233.9 306.977C1254.79 351.188 1269.81 398.718 1277.95 448.561C1268.31 468.917 1229.79 544.442 1147.2 633.014C1139.07 641.574 1139.5 655.27 1148.27 663.188C1152.34 667.039 1157.69 668.965 1162.83 668.965C1168.6 668.965 1174.17 666.825 1178.45 662.117C1227.88 609.259 1262.34 560.895 1284.81 524.515C1284.81 527.59 1284.98 530.665 1285.01 533.895C1285.02 536.549 1285.01 537.567 1285.01 538.584C1284.46 621.348 1265.02 699.723 1230.79 769.595C1209.66 764.514 1188.62 759.101 1167.66 753.356C1073.71 727.631 971.368 693.107 866.627 651.934C1072.57 515.509 1180.79 351.985 1220.82 281.196Z"
          fill="url(#wt-uranus)"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M749.741 0C813.599 0.027 874.841 11.234 931.619 31.768C914.49 51.032 886.055 81.165 845.035 115.989C836.047 123.479 834.763 136.961 842.467 146.163C846.747 151.084 852.739 153.652 858.731 153.652C863.652 153.652 868.575 151.941 872.641 148.517C924.643 104.433 957.599 67.838 973.435 49.006C1000.4 61.453 1026.11 75.995 1050.46 92.519C1056.24 96.46 1061.95 100.51 1067.58 104.67C1027.75 154.128 894.649 306.252 682.609 415.16C672.123 420.51 667.842 433.351 673.192 443.837C677.044 451.327 684.535 455.607 692.238 455.607C695.662 455.607 698.873 454.751 702.083 453.253C923.145 339.619 1059.03 184.468 1100.98 131.61C1125.12 152.737 1147.44 175.891 1167.52 200.813C1177.31 213.027 1186.58 225.68 1195.29 238.737C1171.59 285.255 1061.76 480.036 818.096 632.45C794.251 622.678 770.33 612.584 746.403 602.19C602.995 539.892 466.443 469.914 350.214 399.572C432.503 382.341 511.769 357.37 587.164 324.852C597.864 320.144 603 307.519 598.292 296.605C593.584 285.905 580.958 280.983 570.044 285.477C483.891 322.834 392.213 349.737 296.633 366.193C281.631 356.562 267.052 346.946 252.929 337.363C331.654 139.935 524.565 0.115 749.741 0ZM821.709 162.426C814.647 153.01 801.379 151.085 791.749 157.933C774.629 170.773 756.653 183.185 738.677 195.169C728.619 201.589 725.837 214.857 732.471 224.701C736.537 230.907 743.385 234.33 750.233 234.33C754.299 234.33 758.365 233.26 762.003 230.906C780.835 218.708 799.453 205.654 817.429 192.386C826.844 185.324 828.771 172.056 821.709 162.426Z"
          fill="url(#wt-uranus)"
        />
        <path
          d="M66.505 181.072C86.981 175.063 110.315 173.567 133.441 174.133C158.995 174.759 188.619 178.058 221.406 183.616C210.738 200.461 200.571 217.919 190.853 235.946C116.004 225.222 68.048 229.905 58.294 252.727C30.973 316.654 313.651 498.871 691.204 661.192L700.139 665.022C1081.41 827.977 1412.87 908.111 1441.54 844.371L1441.71 843.996C1451.93 820.066 1418.72 779.561 1353.36 729.73L1356.69 716.781C1361.17 699.391 1364.98 684.329 1368.12 670.404C1399.46 693.104 1426.06 714.936 1446.58 735.366C1462.97 751.689 1478.01 769.588 1487.82 788.54C1497.26 806.77 1506.62 835.719 1493.6 866.174C1480.58 896.629 1453.19 909.871 1433.49 915.651C1413.02 921.66 1389.69 923.156 1366.56 922.59C1319.72 921.444 1259.21 911.313 1190.61 894.757C1052.52 861.429 870.942 799.395 677.96 716.915C484.978 634.435 314.655 546.067 195.133 469.291C135.759 431.152 86.62 394.418 53.422 361.357C37.031 345.034 21.986 327.135 12.178 308.183C2.891 290.238 -6.323 261.906 5.807 231.975L7.021 229.133C20.271 199.681 47.116 186.761 66.505 181.072Z"
          fill="#8FFF40"
        />
      </svg>
    )
  }
  if (source?.startsWith('blum')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="8.5 9 46 46"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M27.533 21.578H33.833V29.962H27.533V21.578Z" fill="#fff" />
        <path d="M27.533 34.155H35.933V38.347H31.733V42.54H27.533V34.155Z" fill="#fff" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M31.733 9H27.533V13.193H14V50.925H27.533V55.118H31.733V50.925H44.8V46.733H49V17.385H44.8V13.193H31.733V9ZM44.8 42.54H40.6V46.733H18.2V17.385H40.6V21.578H44.8V29.963H40.6V34.155H44.8V42.54Z"
          fill="#fff"
        />
      </svg>
    )
  }
  if (source?.startsWith('tonfun')) {
    // tonfun's mark is a raster (3D green arrow) — its only asset.
    return (
      <img
        src={tonfunLogo}
        width={Math.round(size * 1.5)}
        height={Math.round(size * 1.5)}
        alt=""
        aria-hidden="true"
        style={{ display: 'block', objectFit: 'contain' }}
      />
    )
  }
  if (source?.startsWith('gaspump')) {
    // gaspump's mark is a raster (red pixel pump) — its only asset. Рисуется
    // в размер SVG-лого (без 1.4x — растр почти без полей, распирал бейдж).
    return (
      <img
        src={gaspumpLogo}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        style={{ display: 'block', objectFit: 'contain' }}
      />
    )
  }
  if (source?.startsWith('dedust')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="11.5 11 42 42"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M23.079 32.037L18.528 29.404V24.173L23.079 26.807V32.037Z" fill="#FFAA01" />
        <path d="M18.528 29.404L14 26.774V21.539L18.528 24.173V29.404Z" fill="#FC9801" />
        <path d="M27.629 34.668L23.079 32.037V26.807L27.629 29.437V34.668Z" fill="#FFB636" />
        <path d="M32.191 37.299L27.629 34.668V29.437L32.191 32.068V37.299Z" fill="#FC9801" />
        <path d="M23.079 37.266L18.528 34.635V29.404L23.079 32.037V37.266Z" fill="#FC9801" />
        <path d="M18.528 34.635L14 32.004V26.774L18.528 29.406V34.635Z" fill="#FFBD1C" />
        <path d="M27.629 39.898L23.079 37.266V32.037L27.629 34.668V39.898Z" fill="#FFB306" />
        <path d="M32.191 42.529L27.629 39.898V34.668L32.191 37.299V42.529Z" fill="#FFB636" />
        <path d="M23.079 42.496L18.528 39.864V34.635L23.079 37.266V42.496Z" fill="#FFB636" />
        <path d="M18.528 39.864L14 37.233V32.004L18.528 34.635V39.864Z" fill="#FFAA01" />
        <path d="M27.629 45.127L23.079 42.496V37.266L27.629 39.898V45.127Z" fill="#FFBD1C" />
        <path d="M32.191 47.758L27.629 45.127V39.898L32.191 42.529V47.758Z" fill="#FFAA01" />
        <path d="M23.079 47.725L18.528 45.093V39.864L23.079 42.496V47.725Z" fill="#FFB306" />
        <path d="M18.528 45.093L14 42.463V37.233L18.528 39.864V45.093Z" fill="#FC9801" />
        <path d="M27.629 50.357L23.079 47.725V42.496L27.629 45.127V50.357Z" fill="#FC9801" />
        <path d="M32.191 52.988L27.629 50.357V45.127L32.191 47.758V52.988Z" fill="#FFB636" />
        <path d="M41.304 32.037L36.741 34.668V29.437L41.304 26.807V32.037Z" fill="#D18102" />
        <path d="M36.741 34.668L32.191 37.299V32.068L36.741 29.437V34.668Z" fill="#CC7401" />
        <path d="M45.866 29.406L41.304 32.037V26.807L45.866 24.173V29.406Z" fill="#E68A01" />
        <path d="M50.451 26.774L45.866 29.406V24.173L50.451 21.539V26.774Z" fill="#C97901" />
        <path d="M41.304 37.266L36.741 39.898V34.668L41.304 32.037V37.266Z" fill="#CC7401" />
        <path d="M36.741 39.898L32.191 42.529V37.299L36.741 34.668V39.898Z" fill="#E48900" />
        <path d="M45.866 34.635L41.304 37.266V32.037L45.866 29.406V34.635Z" fill="#DB7D01" />
        <path d="M50.451 32.004L45.866 34.635V29.406L50.451 26.774V32.004Z" fill="#E68A01" />
        <path d="M41.304 42.496L36.741 45.127V39.898L41.304 37.266V42.496Z" fill="#D78303" />
        <path d="M36.741 45.127L32.191 47.758V42.529L36.741 39.898V45.127Z" fill="#D17200" />
        <path d="M45.866 39.864L41.304 42.496V37.266L45.866 34.635V39.864Z" fill="#C96E00" />
        <path d="M50.451 37.233L45.866 39.864V34.635L50.451 32.004V37.233Z" fill="#DC7D00" />
        <path d="M41.304 47.725L36.741 50.357V45.127L41.304 42.496V47.725Z" fill="#C47000" />
        <path d="M36.741 50.357L32.191 52.988V47.758L36.741 45.127V50.357Z" fill="#DE7F00" />
        <path d="M45.866 45.093L41.304 47.725V42.496L45.866 39.864V45.093Z" fill="#D68100" />
        <path d="M50.451 42.463L45.866 45.093V39.864L50.451 37.233V42.463Z" fill="#C96E00" />
        <path
          d="M36.741 18.901L32.191 16.267L36.741 13.634L41.304 16.267L36.741 18.901Z"
          fill="#FFEA76"
        />
        <path
          d="M32.191 16.267L27.629 13.634L32.191 11L36.741 13.634L32.191 16.267Z"
          fill="#FFDB2E"
        />
        <path
          d="M41.304 21.539L36.741 18.901L41.304 16.267L45.866 18.901L41.304 21.539Z"
          fill="#FFDB2E"
        />
        <path
          d="M45.866 24.173L41.304 21.539L45.866 18.901L50.451 21.539L45.866 24.173Z"
          fill="#FFEA76"
        />
        <path
          d="M32.191 21.539L27.629 18.901L32.191 16.267L36.741 18.901L32.191 21.539Z"
          fill="#FFDB2E"
        />
        <path
          d="M27.629 18.901L23.079 16.267L27.629 13.634L32.191 16.267L27.629 18.901Z"
          fill="#FFE36A"
        />
        <path
          d="M36.741 24.173L32.191 21.539L36.741 18.901L41.304 21.539L36.741 24.173Z"
          fill="#FFE36A"
        />
        <path
          d="M41.304 26.807L36.741 24.173L41.304 21.539L45.866 24.173L41.304 26.807Z"
          fill="#FFDB2E"
        />
        <path
          d="M27.629 24.173L23.079 21.539L27.629 18.901L32.191 21.539L27.629 24.173Z"
          fill="#FFDE59"
        />
        <path
          d="M23.079 21.539L18.528 18.901L23.079 16.267L27.629 18.901L23.079 21.539Z"
          fill="#FFEA76"
        />
        <path
          d="M32.191 26.807L27.629 24.173L32.191 21.539L36.741 24.173L32.191 26.807Z"
          fill="#FFEA76"
        />
        <path
          d="M36.741 29.437L32.191 26.807L36.741 24.173L41.304 26.807L36.741 29.437Z"
          fill="#FFE346"
        />
        <path
          d="M23.079 26.807L18.528 24.173L23.079 21.539L27.629 24.173L23.079 26.807Z"
          fill="#FED71D"
        />
        <path
          d="M18.528 24.173L14 21.539L18.528 18.901L23.079 21.539L18.528 24.173Z"
          fill="#FFE346"
        />
        <path
          d="M27.629 29.437L23.079 26.807L27.629 24.173L32.191 26.807L27.629 29.437Z"
          fill="#FFE36A"
        />
        <path
          d="M32.191 32.068L27.629 29.437L32.191 26.807L36.741 29.437L32.191 32.068Z"
          fill="#FFDE59"
        />
      </svg>
    )
  }
  return null
}
